import React from "react";

import { faTrash } from "@fortawesome/pro-solid-svg-icons";
import { Grid } from "@material-ui/core";
import { FieldArray, useField } from "formik";

import { SlimButton } from "components/Button";
import { ButtonIcon } from "components/Button/FontAwesomeButton";
import { PreventPropagation } from "components/Events";
import { Button } from "components/Form";
import { BusinessField } from "components/Form/Fields";
import {
  ControlledLifeCycleInput,
  DecimalStringInputProps,
  OptionTogglerField,
  PercentageInputProps,
  withNamespace,
} from "components/Form/FormikControls";
import { Error } from "components/Form/FormikControls/Error";
import { GSTOverrideOptions } from "constants/billing";
import { SplitOrigin, SplitType } from "constants/splits";

import { useFieldValue } from "hooks";

interface SplitsProps {
  namespace?: string;
  splitType: string;
}

export const SplitAllocationForm = ({
  namespace = "",
  splitType,
}: SplitsProps) => {
  const [field, meta] = useField(withNamespace(namespace, "splits"));
  const splits = field.value || [];
  const { error } = meta;
  const type = useFieldValue(withNamespace(namespace, "type"));

  const amountProps =
    type === SplitType.HEADCOUNT
      ? DecimalStringInputProps
      : PercentageInputProps;

  return (
    <FieldArray name={withNamespace(namespace, "splits")}>
      {arrayHelpers => {
        const addNew = () => {
          if (splitType === SplitOrigin.VENDOR) {
            arrayHelpers.push({ shouldChargeGSTOverride: null });
          } else {
            arrayHelpers.push({});
          }
        };

        return (
          <>
            {splits.map((split, i) => (
              <React.Fragment key={split.id || i}>
                <Grid item xs={6} md={5}>
                  <BusinessField
                    label={splitType}
                    name={withNamespace(namespace, `splits.${i}.businessId`)}
                    activeOnly
                  />
                </Grid>
                <Grid item xs={5} md={2}>
                  <ControlledLifeCycleInput
                    {...amountProps}
                    label="Amount"
                    name={withNamespace(namespace, `splits.${i}.amount`)}
                    required
                  />
                </Grid>
                {splitType === SplitOrigin.VENDOR && (
                  <Grid item xs={11} md={4}>
                    <OptionTogglerField
                      name={withNamespace(
                        namespace,
                        `splits.${i}.shouldChargeGSTOverride`,
                      )}
                      label="Manual GST Override"
                      options={GSTOverrideOptions}
                      tooltip="Allows manual override of the GST status for this split. If no override is in place, the calculated GST status of the lot will be used."
                      labelPosition="top"
                    />
                  </Grid>
                )}

                <Grid
                  item
                  container
                  xs={1}
                  alignItems="flex-end"
                  justifyContent="flex-end"
                >
                  <SlimButton
                    onClick={() => arrayHelpers.remove(i)}
                    type="button"
                    backgroundColor="deleteRed"
                  >
                    <ButtonIcon icon={faTrash} />
                  </SlimButton>
                </Grid>
              </React.Fragment>
            ))}
            {error && (
              <Grid item xs={12}>
                <Error>{Array.isArray(error) ? error.join(" ") : error}</Error>
              </Grid>
            )}
            <PreventPropagation onClick={addNew}>
              <Grid item xs={12} container>
                <Button data-tour="add" type="button">
                  Add
                </Button>
              </Grid>
            </PreventPropagation>
          </>
        );
      }}
    </FieldArray>
  );
};
